import { Box, Button, Stack, Text, Tooltip, styled } from "prism-react";
import * as React from "react";

import { Describes } from "./Describes";
import { FormattedError } from "./FormattedError";
import { Tests } from "./Tests";
import { StyledToggleButton } from "./common";
import type { Spec } from "./types";
import { getFailingTests, getSpecTestResults, isEmpty } from "./utils";

interface Props {
  spec: Spec;
  verbose: boolean;
  openSpec: (name: string) => void;
  currentSingleFilePath?: string;
  setSingleFilePathMode: (path: string) => void;
}

const StyledSpecButton = styled("button", {
  padding: "$1 $2",
  color: "$neutral-bg-base",
  fontSize: "inherit",
  fontFamily: "$mono",
  textTransform: "uppercase",
  marginRight: "$2",
  borderRadius: "$1",
});

const StyledFilePathText = styled(Text, {
  color: "$neutral-fg-medium",
  textDecorationStyle: "dotted",
  textDecorationLine: "underline",
});

export const SpecFile: React.FC<Props> = ({
  spec,
  openSpec,
  verbose,
  setSingleFilePathMode,
  currentSingleFilePath,
}) => {
  const [showDetails, setShowDetails] = React.useState(verbose);

  React.useEffect(() => {
    setShowDetails(verbose);
  }, [verbose]);

  if (spec.error) {
    return (
      <Stack key={spec.name}>
        <Stack horizontal>
          <Tooltip content={showDetails ? "Hide details" : "Show details"}>
            <StyledSpecButton
              css={{ backgroundColor: "$informative-error-base" }}
              onClick={() => setShowDetails(!showDetails)}
            >
              Error
            </StyledSpecButton>
          </Tooltip>
          <FilePath
            onClick={(): void => openSpec(spec.name)}
            path={spec.name}
          />
        </Stack>
        <FormattedError error={spec.error} path={spec.name} />
      </Stack>
    );
  }

  if (isEmpty(spec)) {
    return null;
  }

  const tests = Object.values(spec.tests);
  const describes = Object.values(spec.describes);
  const stats = getSpecTestResults(spec);

  return (
    <Box key={spec.name}>
      <Stack
        css={{
          "&:hover ._pin-button": {
            opacity: 1,
          },

          "._pin-button": {
            opacity: currentSingleFilePath === spec.name ? 1 : 0,
            transition: "opacity 0.2s ease-in-out",
          },
        }}
        gap={2}
        horizontal
      >
        <Tooltip content={showDetails ? "Hide details" : "Show details"}>
          {stats.fail > 0 ? (
            <StyledSpecButton
              css={{ backgroundColor: "$informative-error-base" }}
              onClick={() => setShowDetails(!showDetails)}
            >
              Fail
            </StyledSpecButton>
          ) : (
            <StyledSpecButton
              css={{ backgroundColor: "$informative-success-base" }}
              onClick={() => setShowDetails(!showDetails)}
            >
              Pass
            </StyledSpecButton>
          )}
        </Tooltip>

        <FilePath
          onClick={(): void => {
            openSpec(spec.name);
          }}
          path={spec.name}
        />

        <Tooltip
          content={
            currentSingleFilePath === spec.name ? "Unpin" : "Only run this file"
          }
        >
          <StyledToggleButton
            className="_pin-button"
            data-active={currentSingleFilePath === spec.name}
            icon="pin"
            onClick={() => setSingleFilePathMode(spec.name)}
          />
        </Tooltip>
      </Stack>

      {showDetails && <Tests tests={tests} />}

      {showDetails && <Describes describes={describes} />}

      {showDetails &&
        getFailingTests(spec).map((test, index) => {
          return (
            <Box key={index} css={{ padding: "$2 0" }}>
              <Text color="informative-error-base" weight="semibold">
                ● {test.blocks.join(" › ")} › {test.name}
              </Text>
              {test.errors.map((e) => (
                <FormattedError
                  key={`failing-${test.name}-error`}
                  error={e}
                  path={test.path}
                />
              ))}
            </Box>
          );
        })}
    </Box>
  );
};

const FilePath: React.FC<{ onClick: () => void; path: string }> = ({
  onClick,
  path,
}) => {
  const parts = path.split("/");
  const basePath = (parts.slice(0, parts.length - 1).join("/") + "/").slice(1);
  const fileName = parts[parts.length - 1];

  return (
    <Tooltip content="Open test file">
      <Button
        css={{
          fontFamily: "$mono",
          padding: "$1",
          height: "$6",
          "&:hover": {
            backgroundColor: "transparent",
          },
        }}
        onClick={onClick}
        type="button"
      >
        <StyledFilePathText css={{ color: "$neutral-fg-base" }}>
          {basePath}
        </StyledFilePathText>
        <StyledFilePathText css={{ fontWeight: "$semibold" }}>
          {fileName}
        </StyledFilePathText>
      </Button>
    </Tooltip>
  );
};
